/**
 * Filters matches based on group criteria (matchIds or date range)
 * @param {Array} matches - Array of match objects
 * @param {Object} group - Group object with filters
 * @returns {Array} Filtered matches
 */
export const filterMatchesByGroup = (matches, group) => {
	if (!matches || !group || !group.filters) {
		return [];
	}

	const { matchIds, fromDate, toDate } = group.filters;

	// If matchIds are present and not empty, filter by matchIds
	const validMatchIds = matchIds?.filter((id) => id && id.trim() !== "") || [];
	if (validMatchIds.length > 0) {
		return matches.filter((match) => validMatchIds.includes(match.id));
	}

	// Otherwise, filter by date range
	if (fromDate && toDate) {
		// Convert dates to UTC timestamps with proper time boundaries
		const fromDateTime = new Date(`${fromDate}T00:00:00Z`).getTime();
		const toDateTime = new Date(`${toDate}T23:59:59Z`).getTime();

		return matches.filter((match) => {
			const kickoffTime = new Date(match.kickoffAt).getTime();
			return kickoffTime >= fromDateTime && kickoffTime <= toDateTime;
		});
	}

	return [];
};

/**
 * Gets the current active group based on today's date
 * @param {Array} groups - Array of group objects
 * @returns {Object|null} Current active group or null
 */
export const getCurrentGroup = (groups) => {
	if (!groups || groups.length === 0) {
		return null;
	}

	const now = new Date();
	const todayStr = now.toISOString().split("T")[0]; // YYYY-MM-DD format

	// First, check if we're currently in an active multi-day period
	const currentGroup = groups.find((group) => {
		if (!group.filters) return false;

		const { fromDate, toDate } = group.filters;
		if (!fromDate || !toDate) return false;

		return todayStr >= fromDate && todayStr <= toDate;
	});

	if (currentGroup) {
		return currentGroup;
	}

	// If not in a current period, find the next upcoming group (AFTER today)
	const upcomingGroup = groups.find((group) => {
		if (!group.filters || !group.filters.fromDate) return false;
		return group.filters.fromDate > todayStr;
	});

	if (upcomingGroup) {
		return upcomingGroup;
	}

	// If no upcoming or current groups, return the most recent past group
	const pastGroups = groups.filter((group) => {
		if (!group.filters || !group.filters.toDate) return false;
		return group.filters.toDate < todayStr;
	});

	if (pastGroups.length > 0) {
		// Sort by toDate descending and return the most recent
		return pastGroups.sort((a, b) => b.filters.toDate.localeCompare(a.filters.toDate))[0];
	}

	// Fallback to last group if all are in the past
	return groups[groups.length - 1];
};

/**
 * Determines if the season has ended (no current or future groups available)
 * @param {Array} groups - Array of group objects
 * @returns {boolean} True if season has ended, false otherwise
 */
export const isSeasonEnded = (groups) => {
	if (!groups || groups.length === 0) {
		return false;
	}

	const now = new Date();
	const todayStr = now.toISOString().split("T")[0]; // YYYY-MM-DD format

	// Check if there are any upcoming groups (AFTER today)
	const hasUpcomingGroups = groups.some((group) => {
		if (!group.filters || !group.filters.fromDate) return false;
		return group.filters.fromDate > todayStr;
	});

	if (hasUpcomingGroups) {
		return false;
	}

	// Check if we're currently in a multi-day period
	const hasCurrentGroup = groups.some((group) => {
		if (!group.filters) return false;

		const { fromDate, toDate } = group.filters;
		if (!fromDate || !toDate) return false;

		return todayStr >= fromDate && todayStr <= toDate;
	});

	// Season has ended if there are no upcoming groups and no current group
	return !hasCurrentGroup;
};

/**
 * Gets matches for a specific group
 * @param {Array} matches - Array of all matches
 * @param {Array} groups - Array of all groups
 * @param {String} groupId - ID of the group to filter by
 * @returns {Array} Filtered matches for the specified group
 */
export const getMatchesForGroup = (matches, groups, groupId) => {
	const group = groups.find((g) => g.groupId === groupId);
	if (!group) {
		return [];
	}

	return filterMatchesByGroup(matches, group);
};

/**
 * Checks if the user has made predictions for the filtered matches
 * @param {Array} filteredMatches - Array of filtered match objects
 * @param {Array} userPredictions - Array of user prediction objects
 * @returns {boolean} True if user has made predictions for any of the filtered matches
 */
export const hasPredictionsForMatches = (filteredMatches, userPredictions) => {
	if (!filteredMatches || !userPredictions || filteredMatches.length === 0 || userPredictions.length === 0) {
		return false;
	}

	// Get all match IDs from filtered matches
	const matchIds = filteredMatches.map((match) => match.id);

	// Check if any user predictions exist for these matches
	return userPredictions.some((prediction) => matchIds.includes(prediction.matchId));
};
